import { z } from 'zod';
import { US_STATE_CODES } from '../enums';
import { objectId } from './common';
import type { Paged } from './common';
import type { UserRef } from './users';

/**
 * Commission matrix (PC792) as the reference has it (product inventory, appendix 13.14): a
 * matrix holds, for each state and plan, one line per category with a flat amount and a
 * percent of the plan price; the greater of the two is paid. A Recurring matrix adds up to 11
 * further months, each with its own amounts ("12 months: 11 recurring and 1 as Enrollment").
 */

/** The categories of the reference tenant. "Add Category" was not built. */
export const COMMISSION_CATEGORIES = ['Prepaid Enrollment', 'Plan Purchase'] as const;
export type CommissionCategory = (typeof COMMISSION_CATEGORIES)[number];
export const MATRIX_COMMISSION_TYPES = ['Onetime', 'Recurring'] as const;
export type MatrixCommissionType = (typeof MATRIX_COMMISSION_TYPES)[number];
/** "Months" {Select no of month, 1 to 11}. */
export const MATRIX_MAX_MONTHS = 11;
/** The quick filters of the "Matrix List". */
export const MATRIX_FILTERS = ['All', 'Active', 'Inactive', 'Recurring', 'One Time'] as const;
export type MatrixFilter = (typeof MATRIX_FILTERS)[number];

const yesNo = z.enum(['Yes', 'No']);

/** One amount of the dialog: a month (0 = "Commission for current month") and a category. */
export const matrixRateSchema = z.object({
  month: z.number().int().min(0).max(MATRIX_MAX_MONTHS),
  category: z.enum(COMMISSION_CATEGORIES),
  /** "$": at most five characters on the reference; here at most $999.99. */
  flatCents: z.number().int().min(0, 'Amount cannot be negative').max(99_999, 'At most $999.99'),
  /** "%", in hundredths of a percent (10% = 1000). */
  percentBp: z.number().int().min(0, 'Percent cannot be negative').max(10_000, 'At most 100%'),
});
export type MatrixRate = z.infer<typeof matrixRateSchema>;

const linesFields = {
  commissionType: z.enum(MATRIX_COMMISSION_TYPES, {
    errorMap: () => ({ message: 'Select Matrix commission type' }),
  }),
  months: z.number().int().min(0).max(MATRIX_MAX_MONTHS).default(0),
  recurringPlan: yesNo.default('No'),
  recurringDevice: yesNo.default('No'),
  rates: z.array(matrixRateSchema).max(COMMISSION_CATEGORIES.length * (MATRIX_MAX_MONTHS + 1)),
};

const checkRecurring = (
  v: { commissionType: MatrixCommissionType; months: number },
  ctx: z.RefinementCtx,
) => {
  if (v.commissionType === 'Recurring' && v.months < 1)
    ctx.addIssue({ code: 'custom', path: ['months'], message: 'Select no of month' });
};

/** "Add matrix commission (PC1243)": a new matrix with the lines of one state and plan. */
export const matrixCreateSchema = z
  .object({
    name: z.string().trim().min(1, 'Matrix name is required').max(100, 'At most 100 characters'),
    state: z.enum(US_STATE_CODES, { errorMap: () => ({ message: 'Select State' }) }),
    /** A plan, or `all` for every plan offered in the state (this project's addition). */
    planId: z.union([objectId, z.literal('all')], {
      errorMap: () => ({ message: 'Select Plan' }),
    }),
    ...linesFields,
  })
  .superRefine(checkRecurring);
export type MatrixCreateInput = z.input<typeof matrixCreateSchema>;

/**
 * "Update matrix commission (PC1243)" and "Add state to matrix (PC1243)": the type and the
 * recurring settings of the matrix, and the lines of one state and plan.
 */
export const matrixLinesSchema = z
  .object({
    state: z.enum(US_STATE_CODES, { errorMap: () => ({ message: 'Select State' }) }),
    planId: z.union([objectId, z.literal('all')], {
      errorMap: () => ({ message: 'Select Plan' }),
    }),
    ...linesFields,
  })
  .superRefine(checkRecurring);
export type MatrixLinesInput = z.input<typeof matrixLinesSchema>;

/** "Copy matrix": the copy's name (the reference's dialog was not seen). */
export const matrixCopySchema = z.object({
  name: z.string().trim().min(1, 'Matrix name is required').max(100, 'At most 100 characters'),
});
export const matrixStatusSchema = z.object({ status: z.enum(['Active', 'Inactive']) });

export const matrixListQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  filter: z.enum(MATRIX_FILTERS).default('All'),
});
export const matrixLinesQuerySchema = z.object({
  state: z.enum(US_STATE_CODES).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(10),
});
export const matrixPlansQuerySchema = z.object({
  state: z.enum(US_STATE_CODES, { errorMap: () => ({ message: 'Select State' }) }),
});
export const matrixRatesQuerySchema = z.object({
  state: z.enum(US_STATE_CODES),
  planId: objectId,
});

/** One row of the "Matrix List". */
export interface CommissionMatrixDto {
  id: string;
  name: string;
  commissionType: MatrixCommissionType;
  /** Recurring only; null on a Onetime matrix (the list shows a dash). */
  months: number | null;
  recurringPlan: 'Yes' | 'No' | null;
  recurringDevice: 'Yes' | 'No' | null;
  /** How many users of each level have this matrix. */
  totals: { employee: number; retailer: number; distributor: number; master: number };
  status: 'Active' | 'Inactive';
  createdAt: string;
  createdBy: UserRef | null;
  updatedAt: string;
}

/** "Manage Commission Matrix": the cards and the "Matrix List". */
export interface CommissionMatrixListDto {
  cards: {
    total: number;
    active: number;
    inactive: number;
    /** "Avg. Total Employees": employees per matrix, one decimal. */
    avgEmployees: number;
    lastUpdated: string | null;
  };
  data: CommissionMatrixDto[];
}

/** One line of "Update commission matrix": # | Category | Plan | State | Amount ($/%). */
export interface CommissionLineDto {
  id: string;
  category: CommissionCategory;
  planId: string;
  plan: string;
  state: string;
  flatCents: number;
  percentBp: number;
}

export interface CommissionMatrixDetailDto {
  matrix: CommissionMatrixDto;
  /** The states the matrix has lines for. */
  states: string[];
  lines: Paged<CommissionLineDto>;
  /** The recurring months' amounts, which apply to every state and plan of the matrix. */
  recurring: MatrixRate[];
}

/** The amounts of one state and plan, for the update dialog. */
export interface CommissionRatesDto {
  state: string;
  planId: string;
  plan: string;
  rates: MatrixRate[];
}

/** "$15.00 / 10%" as the Amount ($/%) cell shows it. */
export const percentLabel = (bp: number): string =>
  `${(bp / 100).toFixed(2).replace(/\.?0+$/, '')}%`;

/* ------------- Categories, redeem and limit rules (walk of 2026-10-27) ------------- */

/**
 * "View category": the commission categories with their type. These two are built in (the
 * reference's defaults, amount 0); its "Add Category" types (TopUp, Data topUp, Bill Pay,
 * Default fee, Other) are not built: what such a category earns was not seen.
 */
export const COMMISSION_CATEGORY_LIST = [
  { name: 'Plan Purchase', type: 'Plan_purchase', amountCents: 0 },
  { name: 'Prepaid Enrollment', type: 'Enrollment', amountCents: 0 },
] as const;

/**
 * "Add Category" (switch `commission-add-category`, walk of 2026-10-27): Category name* (50),
 * Type*, Amount* (5 characters). Assumption, the reference's meaning was not seen: an added
 * category pays its Amount, as a flat sum on top of the matrix, to every user of the selling chain
 * who has an Active commission matrix, each time its event happens: Enrollment, a completed
 * enrollment; TopUp, a top-up; Data topUp, a top-up that adds data only; Bill Pay, a plan purchase
 * (renewal or plan change). Default fee and Other are listed only and earn nothing by themselves.
 */
export const COMMISSION_CATEGORY_TYPES = [
  'Enrollment',
  'TopUp',
  'Data topUp',
  'Bill Pay',
  'Default fee',
  'Other',
] as const;
export type CommissionCategoryType = (typeof COMMISSION_CATEGORY_TYPES)[number];

export const commissionCategorySchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Category name is required')
    .max(50, 'Category name may have up to 50 characters'),
  type: z.string().refine((v) => (COMMISSION_CATEGORY_TYPES as readonly string[]).includes(v), {
    message: 'Select Type',
  }),
  // "Amount*" takes up to 5 characters, so at most 99999 cents ($999.99).
  amountCents: z
    .number({ invalid_type_error: 'Amount is required' })
    .int()
    .min(0, 'Amount cannot be negative')
    .max(99_999, 'Amount may be at most 999.99'),
});
export type CommissionCategoryInput = z.input<typeof commissionCategorySchema>;

export interface CommissionCategoryDto {
  /** Null for the two built-in categories. */
  id: string | null;
  name: string;
  type: string;
  amountCents: number;
  createdBy: string;
  createdAt: string | null;
}

export const REDEEM_TYPES = ['Manual', 'Automatic'] as const;
export type RedeemType = (typeof REDEEM_TYPES)[number];

/** "Set Matrix Commission Redeem Rule" (switch `commission-redeem`). */
export const redeemRuleSchema = z
  .object({
    redeemType: z.enum(REDEEM_TYPES, { required_error: 'Select Redeem Type' }),
    thresholdCents: z.number().int().min(0).max(1_000_000).nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (v.redeemType === 'Automatic' && v.thresholdCents === null)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['thresholdCents'],
        message: 'Please enter a valid amount (0 - 10,000).',
      });
  });
export type RedeemRuleInput = z.input<typeof redeemRuleSchema>;

export interface RedeemRuleDto {
  redeemType: RedeemType;
  thresholdCents: number | null;
}

/** "Set Commission Limit Rule": per master agent, a percentage (0-200) and a flat amount. */
export const commissionLimitSchema = z.object({
  masterUserId: z.string().regex(/^[a-f\d]{24}$/i, 'Select Master Agent'),
  /** Hundredths of a percent: 200% is 20000. */
  percentBp: z.number().int().min(0, 'Max allowed is 200%.').max(20_000, 'Max allowed is 200%.'),
  flatCents: z
    .number()
    .int()
    .min(0, 'Max allowed is 100000.')
    .max(10_000_000, 'Max allowed is 100000.'),
});
export type CommissionLimitInput = z.input<typeof commissionLimitSchema>;

export interface CommissionLimitDto {
  master: { id: string; name: string; loginId: string };
  percentBp: number;
  flatCents: number;
}

/** A user's commission: earned, redeemed into the dealer wallet, and what is left to redeem. */
export interface CommissionBalanceDto {
  earnedCents: number;
  redeemedCents: number;
  balanceCents: number;
  redeemType: RedeemType;
  thresholdCents: number | null;
}
